import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Grid, IconButton, InputBase, SxProps, Tooltip } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';
import { FC, ReactNode, Ref } from 'react';

const searchButtonSx: SxProps = {
    '&:hover': {
        bgcolor: 'primary.dark',
    },
    bgcolor: 'primary.main',
    borderRadius: 0.5,
    boxShadow: 0,
    color: 'grey.800',
    mr: 1,
};
interface SearchBoxProps {
    disabled?: boolean;
    endAdornment?: ReactNode;
    expandable?: boolean;
    inputRef?: Ref<HTMLInputElement | HTMLTextAreaElement>;
    maxRows?: number;
    onChange?: (searchValue: string) => void;
    onReset?: () => void;
    onSearch?: () => void;
    placeholder?: string;
    showResetWhenEmpty?: boolean;
    startAdornment?: ReactNode;
    value?: string;
}

const SearchBox: FC<SearchBoxProps> = ({
    expandable,
    onChange,
    onSearch,
    maxRows = 1,
    placeholder,
    value,
    onReset,
    disabled,
    showResetWhenEmpty,
    endAdornment,
    startAdornment,
    inputRef,
}) => {
    const { t } = useTranslation('search');

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange(event?.target?.value ?? '');
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // Only trigger search on Enter without modifier keys (Shift, Ctrl, Alt, Meta)
        if (
            'Enter' === event.key &&
            !event.shiftKey &&
            !event.ctrlKey &&
            !event.altKey &&
            !event.metaKey &&
            onSearch
        ) {
            event.preventDefault();
            onSearch();
        }
    };

    const handleResetSearch = () => {
        if (onChange) {
            onChange('');
        }
        if (onReset) {
            onReset();
        }
    };

    const hasSearchValue = Boolean(value);
    const inputEndAdornment =
        hasSearchValue || endAdornment ? (
            <>
                {(hasSearchValue || showResetWhenEmpty) && (
                    <Tooltip title={t('closeHint')}>
                        <IconButton onClick={handleResetSearch} sx={{ alignSelf: 'flex-end' }}>
                            <CloseIcon sx={{ color: 'grey.400' }} />
                        </IconButton>
                    </Tooltip>
                )}
                {endAdornment}
            </>
        ) : undefined;

    return (
        <>
            <Grid item xs>
                <InputBase
                    data-test-id="inputSearch"
                    disabled={disabled}
                    endAdornment={inputEndAdornment}
                    inputProps={{ 'aria-label': 'search' }}
                    inputRef={inputRef}
                    maxRows={maxRows}
                    minRows={1}
                    multiline={expandable}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                    placeholder={placeholder || t('searchBox.placeholder')}
                    startAdornment={startAdornment}
                    sx={{
                        border: 'none',
                        pl: startAdornment ? 0 : 2,
                        width: '100%',
                        ...(expandable && {
                            '& textarea': {
                                resize: 'none',
                            },
                            minHeight: 40,
                        }),
                    }}
                    type="search"
                    value={value}
                />
            </Grid>
            <Grid item>
                <IconButton
                    data-test-id="submitSearch"
                    disabled={disabled}
                    onClick={onSearch}
                    sx={searchButtonSx}
                >
                    <SearchIcon />
                </IconButton>
            </Grid>
        </>
    );
};

export default SearchBox;
